import { Copy, MoreHorizontal, PackageCheck, PackageX, Pencil, Trash2 } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { updateItem, type Item } from "../../../lib/sales";
import { Pop, useClosePops } from "../../shell/Pop";
import { useToast } from "../../ui";

/**
 * The 44px column at the end of an item row.
 *
 * Only entries that do something. There is no item detail page - an item is a
 * saved line, not a record you open - so editing happens in the modal this menu
 * opens, and "duplicate" opens the same modal prefilled rather than writing a
 * silent copy the operator never got to name.
 *
 * The whole menu is manage-only; a reader sees an empty track instead, because
 * every entry here writes.
 */
export function ItemRowMenu({
  item,
  onEdit,
  onDuplicate,
  onDelete,
  onChanged,
}: {
  item: Item;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onChanged: (next: Item) => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  async function toggleActive() {
    close();
    try {
      const next = await updateItem(item.id, { active: !item.active });
      onChanged(next);
      toast(t(next.active ? "sales.items.active" : "sales.items.inactive"));
    } catch {
      toast(t("common.error"));
    }
  }

  return (
    // Above the row-covering overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onEdit();
          }}
        >
          <Pencil className="size-4" />
          {t("sales.items.edit")}
        </button>
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            onDuplicate();
          }}
        >
          <Copy className="size-4" />
          {t("sales.items.duplicate")}
        </button>

        <div className="crm-menu-sep" />
        <button type="button" className="crm-mi" onClick={() => void toggleActive()}>
          {item.active ? <PackageX className="size-4" /> : <PackageCheck className="size-4" />}
          {t(item.active ? "sales.items.deactivate" : "sales.items.activate")}
        </button>

        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            onDelete();
          }}
        >
          <Trash2 className="size-4" />
          {t("common.delete")}
        </button>
      </Pop>
    </span>
  );
}
